import {FC, useState} from "react";
import {MedicationsTableProps, times} from "./types";
import {useDispatch, useSelector} from "react-redux";
import {getCurrentSchedules} from "../../../../../../../redux/selectors/patientsSelector";
import {IMedication, ToastType} from "../../../../../../../utils/interfaces";
import {formatMedicationName, getDates} from "./utils";
import classes from './MedicationsTable.module.scss';
import {Button, Dialog, DialogTitle, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography} from "@mui/material";
import {format, isBefore, isToday, parse} from "date-fns";
import {SET_TOAST} from "../../../../../../../redux/actions";

type InfoItemProps = {
  name: string;
  value: string;
}

const InfoItem: FC<InfoItemProps> = ({name, value}) => {
  return (
    <div>
      <Typography color="GrayText" variant="caption">{name}: </Typography>
      <Typography variant="body1">{value}</Typography>
    </div>
  )
}

type MenuDataType = {
  selectedMedication: null | IMedication;
  dialog: {
    open: boolean;
  };
  menu: {
    anchorEl: null | HTMLDivElement;
  };
}


export const MedicationsTable: FC<MedicationsTableProps> = ({onEditMedication, onRemoveMedication, period}) => {
  const patientMedications: Record<string, IMedication[]> = useSelector(getCurrentSchedules);
  const datesArray = getDates(period);
  const dispatch = useDispatch();

  const [{selectedMedication, dialog: {open}, menu: {anchorEl}}, setMenuData] = useState<MenuDataType>({
    selectedMedication: null,
    dialog: {
      open: false,
    },
    menu: {
      anchorEl: null,
    },
  });

  const handleClick = (data: IMedication, event: React.MouseEvent<HTMLDivElement>) => {
    setMenuData(prev => ({
      ...prev,
      selectedMedication: data,
      menu: {
        anchorEl: event.currentTarget
      }
    }));
  };

  const handleClose = () => {
    setMenuData({
      selectedMedication: null,
      menu: {
        anchorEl: null,
      },
      dialog: {
        open: false,
      }
    });
  };

  const handleEdit = () => {
    if (selectedMedication) {
      const startDate = parse(selectedMedication.startDate, 'yyyy-MM-dd', new Date());
      const endDate = parse(selectedMedication.endDate, 'yyyy-MM-dd', new Date());

      if (isBefore(startDate, new Date()) && isBefore(endDate, new Date()) && !isToday(endDate)) {
        dispatch({
          type: SET_TOAST,
          payload: {
            open: true,
            message: "You can't change finished medications",
            type: ToastType.ERROR,
          },
        })
        return;
      }

      handleClose();
      setTimeout(() => onEditMedication(selectedMedication), 100);
    }
  }

  const handleDelete = () => {
    if (selectedMedication) {
      handleClose();
      setTimeout(() => onRemoveMedication(selectedMedication), 100);
    }
  }

  const handleInfo = () => {
    if (selectedMedication) {
      setMenuData(prev => ({
        ...prev,
        menu: {
          anchorEl: null,
        },
      }));

      setTimeout(() => {
        setMenuData(prev => ({
          ...prev,
          dialog: {
            open: true,
          }
        }))
      }, 100);
    }
  }

  return (
    <>
      <TableContainer className={classes.tableContainer} component={Paper}>
        <Table stickyHeader aria-label="simple table">
          <TableHead>
            <TableRow classes={{
              root: classes.tableHeader
            }}>
              <TableCell className={classes.stickyColumn}></TableCell>
              {datesArray.map(date => (
                <TableCell className={classes.tableCell} align="center" key={date}>
                  <Typography variant="subtitle2">{format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM')}</Typography>
                  <Typography variant="subtitle2">{format(parse(date, 'yyyy-MM-dd', new Date()), 'yyyy')}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {times.map(time => {
              return (
                <TableRow key={time}>
                  <TableCell size="small" className={classes.stickyColumn} component="th" scope="row">
                    <Typography variant="subtitle2">{time}</Typography>
                  </TableCell>
                  {datesArray.map(date => {
                    const medicationsForCurrentDate = patientMedications[date] ?? [];
                    const medicationForCurrentTime = medicationsForCurrentDate.filter(medication => medication.times.some(medicationTime => {
                      const splittedMedicationTime = medicationTime.split(':');

                      return splittedMedicationTime[0] === time.split(':')[0];
                    }));

                    return (
                      <TableCell key={date} className={classes.tableCell}>
                        <div className={classes.medicationContainer}>
                          {medicationForCurrentTime.map(medication => (
                            <Tooltip title={medication.name}>
                              <div onClick={(event) => handleClick(medication, event)} key={medication.id} className={classes.medication} style={{backgroundColor: medication.colorHex}}>
                                <Stack direction="column">
                                  <Typography noWrap variant="body1">
                                    {formatMedicationName(medication.name)} {medication.dosageValue} {medication.dosageUnit}
                                  </Typography>
                                </Stack>
                              </div>
                            </Tooltip>
                          ))}
                        </div>
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog onClose={handleClose} open={open}>
        <DialogTitle sx={{textAlign: 'center'}}>{selectedMedication?.name}</DialogTitle>
        <div style={{paddingRight: 32, paddingLeft: 32}}>
          <Stack direction="column" spacing={2}>
            <Stack direction="column">
              <InfoItem name="Type" value={selectedMedication?.type ?? ''} />
              <InfoItem name="Dosage" value={`${selectedMedication?.dosageValue} ${selectedMedication?.dosageUnit}`} />
              <InfoItem name="Frequency" value={selectedMedication?.times.join(', ') ?? ''} />
              <InfoItem name="Duration" value={`${selectedMedication?.startDate} - ${selectedMedication?.endDate}`} />
            </Stack>
            <div style={{padding: 8, paddingBottom: 16}}>
              <div style={{display: 'flex', flexDirection: 'row', gap: 12}}>
                <Button
                  className={classes.action}
                  onClick={handleEdit}
                  sx={{flex: 1}}
                  variant="contained">
                  Edit
                </Button>
                <Button
                  className={classes.action}
                  sx={{flex: 1}}
                  onClick={handleDelete}
                  color="error"
                  variant="outlined">
                  Remove
                </Button>
              </div>
            </div>
          </Stack>
        </div>
      </Dialog>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
      >
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleInfo}>Info</MenuItem>
        <MenuItem sx={{color: 'red'}} onClick={handleDelete}>Remove</MenuItem>
      </Menu>
    </>
  );
}